"use client";

import { Link } from "@/i18n/navigation";
import Image from "next/image";

export interface PortfolioItem {
  id: string;
  title_en: string;
  title_ar: string | null;
  slug: string;
  service_line: string;
  summary_en: string | null;
  summary_ar: string | null;
  cover_image_url: string | null;
  challenge_en: string | null;
  challenge_ar: string | null;
  solution_en: string | null;
  solution_ar: string | null;
  results_en: string | null;
  results_ar: string | null;
  client_name: string | null;
  metric_1_value: string | null;
  metric_1_label: string | null;
  metric_2_value: string | null;
  metric_2_label: string | null;
  metric_3_value: string | null;
  metric_3_label: string | null;
  published: boolean;
  display_order: number;
  created_at: string;
}

const serviceLabelMap: Record<string, { en: string; ar: string }> = {
  cybersecurity: { en: "Cybersecurity", ar: "الأمن السيبراني" },
  education: { en: "Education", ar: "التعليم" },
  software: { en: "Software", ar: "البرمجيات" },
};

export function PortfolioCard({
  item,
  locale,
}: {
  item: PortfolioItem;
  locale: string;
}) {
  const title =
    locale === "ar" ? item.title_ar || item.title_en : item.title_en;
  const summary =
    locale === "ar" ? item.summary_ar || item.summary_en : item.summary_en;
  const serviceBadge = serviceLabelMap[item.service_line]
    ? locale === "ar"
      ? serviceLabelMap[item.service_line].ar
      : serviceLabelMap[item.service_line].en
    : item.service_line;

  return (
    <Link
      href={`/portfolio/${item.slug}`}
      className="group block rounded-lg border border-(--fm-border) bg-(--fm-surface) overflow-hidden hover:border-(--fm-accent)/40 transition-colors"
    >
      {/* Cover image or placeholder gradient */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {item.cover_image_url ? (
          <Image
            src={item.cover_image_url}
            alt={title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-(--fm-accent)/20 to-(--fm-accent)/5" />
        )}
        {/* Service line badge */}
        <span className="absolute top-3 left-3 rtl:left-auto rtl:right-3 px-2.5 py-1 text-xs font-medium rounded-full bg-(--fm-background)/80 text-(--fm-accent) backdrop-blur-sm">
          {serviceBadge}
        </span>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-lg font-bold text-(--fm-text-primary) mb-1 group-hover:text-(--fm-accent) transition-colors">
          {title}
        </h3>
        {item.client_name && (
          <p className="text-xs text-(--fm-text-secondary) mb-2">
            {item.client_name}
          </p>
        )}
        {summary && (
          <p className="text-sm text-(--fm-text-secondary) line-clamp-2 leading-relaxed">
            {summary}
          </p>
        )}
      </div>
    </Link>
  );
}
